import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../providers/DataProvider';

// Minimal, accessible command palette inspired by common patterns
export default function CommandPalette() {
  const navigate = useNavigate();
  const { clients = [], suppliers = [], brokers = [] } = useData();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  // keyboard shortcut: Ctrl+K / Cmd+K
  useEffect(() => {
    const onKeyDown = (e) => {
      const isModK = (e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K');
      if (isModK) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (open) {
        if (e.key === 'Escape') {
          setOpen(false);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveIndex((i) => Math.min(i + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (results[activeIndex]) {
            results[activeIndex].action();
            setOpen(false);
          }
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const baseCommands = useMemo(() => ([
    { label: 'Dashboard', path: '/' },
    { label: 'Operaciones', path: '/transactions' },
    { label: 'Clientes', path: '/clients' },
    { label: 'Proveedores', path: '/suppliers' },
    { label: 'Brokers', path: '/brokers' },
    { label: 'Sucursales', path: '/branches' },
    { label: 'Calendario de Cobros', path: '/pending-cash-calendar' },
    { label: 'Reportes', path: '/reports' },
    { label: 'Nueva Operación', action: () => {
      navigate('/transactions');
      // broadcast to open creation wizard
      window.dispatchEvent(new CustomEvent('open-new-transaction'));
    }},
  ]), [navigate]);

  const entityMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const mapToCmd = (item, type) => ({
      label: `${type}: ${item.name || item.company_name || item.id}`,
      action: () => navigate(`/${type === 'Cliente' ? 'clients' : type === 'Proveedor' ? 'suppliers' : 'brokers'}`),
    });
    const c = clients.filter(c => (c.name || '').toLowerCase().includes(q)).slice(0, 5).map(it => mapToCmd(it, 'Cliente'));
    const s = suppliers.filter(s => (s.name || '').toLowerCase().includes(q)).slice(0, 5).map(it => mapToCmd(it, 'Proveedor'));
    const b = brokers.filter(b => (b.name || '').toLowerCase().includes(q)).slice(0, 5).map(it => mapToCmd(it, 'Broker'));
    return [...c, ...s, ...b];
  }, [query, clients, suppliers, brokers, navigate]);

  const navCommands = useMemo(() => baseCommands.map(c => ({
    label: c.label,
    action: c.action || (() => navigate(c.path)),
  })), [baseCommands, navigate]);

  const results = query ? entityMatches.length ? entityMatches : [{ label: 'Sin resultados', action: () => {} }] : navCommands;

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/40" aria-modal="true" role="dialog">
      <div className="mx-auto mt-24 max-w-xl rounded-xl bg-white border border-gray-200">
        <div className="border-b p-3">
          <input
            autoFocus
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
            placeholder="Buscar o ir a… (Ctrl+K)"
            className="w-full outline-none text-sm p-2"
            aria-label="Buscar o ir a"
          />
        </div>
        <ul className="max-h-80 overflow-y-auto py-1">
          {results.map((r, i) => (
            <li key={i}>
              <button
                className={`w-full text-left px-3 py-2 text-sm ${i === activeIndex ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => { r.action(); setOpen(false); }}
              >
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
